import * as React from 'react'
import ReactResizeDetector from 'react-resize-detector'
import loadable from '@loadable/component'
import debounce from 'lodash.debounce'

import { CarouselItems } from './components/CarouselItems'
import { Controls as ControlsComponent } from './components/Controls'

const ChevronLeft = loadable(
  () => import('@thg-commerce/gravity-icons/src/components/ChevronLeft'),
  { ssr: true, fallback: <div style={{ width: 24, height: 24 }} /> },
)
const ChevronRight = loadable(
  () => import('@thg-commerce/gravity-icons/src/components/ChevronRight'),
  { ssr: true, fallback: <div style={{ width: 24, height: 24 }} /> },
)
const SvgIcon = loadable(
  () => import('@thg-commerce/gravity-icons/src/components/SvgIcon'),
  { ssr: true, fallback: <div style={{ width: 24, height: 24 }} /> },
)

import { Property } from 'csstype'

import { HorizontalAlignment } from '@thg-commerce/gravity-theme/alignments'
import {
  BreakpointArray,
  breakpointUtils,
} from '@thg-commerce/gravity-theme/breakpoints'
import { Margin } from '@thg-commerce/gravity-theme/margin'
import { Padding } from '@thg-commerce/gravity-theme/padding'

import { useSwipe, wrapSwipe } from './hooks/useSwipe'
import { carouselReducer } from './reducer'
import {
  BottomControlsWrapper,
  CarouselItemContainer,
  CarouselWrapper,
  Container,
  Control,
  ControlsWrapper,
  OverflowWrapper,
  PageIndicator,
  PageIndicatorGrid,
  PageIndicatorGridItem,
  PageIndicatorWrapper,
} from './styles'
import { CarouselIndicatorStyle, ChevronIconStyle } from './theme'
import {
  AmpCarouselProps,
  CarouselButtonPlacement,
  CarouselI18nText,
  CarouselImageCrop,
  CarouselReducerActionTypes,
  CarouselState,
} from './types'

export type CarouselProps = {
  items: JSX.Element[]
  i18n?: CarouselI18nText
  indicatorStyle?: CarouselIndicatorStyle
  dotHorizontalMargin?: number
  indicatorWrapperMargin?: Margin
  chevronIconStyle?: ChevronIconStyle
  // @deprecated - use controls.placement
  buttonPlacement?: CarouselButtonPlacement
  controls?: {
    visible?: BreakpointArray<boolean>
    placement?: CarouselButtonPlacement
    size?: number
    alternativeChevronStyling?: boolean
    controlBorderWidth?: string
    controlBorderColour?: Property.Color
    controlDisabledBackgroundColor?: Property.Color
    controlHoverBackgroundColor?: Property.Color
  }
  controlSize?: number
  autoPlay?: boolean
  autoPlaySlideDuration?: number
  inactiveSlideFullOpacity?: boolean
  hideControls?: boolean[] | boolean
  loopSlides?: boolean
  slideTo?: number
  isZoomModal?: boolean
  zoomClickable?: boolean
  zoomOnClick?: (index: number) => void
  onChange?: (index: number) => void
  className?: string
  style?: any
  isAmp?: boolean
  ampProps?: AmpCarouselProps
  swipeable?: boolean
  itemGapSpacing?: number
  ignoreGapOffset?: boolean
  enableSlidePreview?: boolean
  enableDualSidePreview?: boolean
  shrinkable?: boolean
  overflow?: BreakpointArray<string>
  fullWidthCarousel?: boolean
  removeControlsOnSingleSlide?: boolean
  onSwipe?: () => void
  onNavClick?: (direction: 'left' | 'right') => void
  onThumbnailClick?: () => void
  indicatorPadding?: Padding
  enableWhiteControls?: boolean
  customTransform?: number
  mobileImageCrop?: CarouselImageCrop
  enableCarouselSignifier?: boolean
  enableCarouselWrapAround?: boolean
  highlightPageIndicator?: boolean
} & InitialCarouselState

type ElementProp = {
  height: number
  width?: number
}
interface InitialCarouselState {
  itemsPerSlide: number | number[]
  items: JSX.Element[]
  hideSlidePreview?: boolean
  hideControlsOnSingleSlide?: boolean
  slideTo?: number
  hideControls?: boolean[] | boolean
}

const DEBOUNCE_INTERVAL_MS = 50

const carouselDirections = {
  left: { slide: -1, action: CarouselReducerActionTypes.SlideLeft },
  right: { slide: 1, action: CarouselReducerActionTypes.SlideRight },
}

const getInitialState = (initialProps: InitialCarouselState): CarouselState => {
  let itemsPerSlide: number

  if (typeof initialProps.itemsPerSlide === 'number') {
    itemsPerSlide = initialProps.itemsPerSlide
  } else {
    let breakpointIndex = -1

    if (typeof window !== 'undefined') {
      Object.values(breakpointUtils.map).forEach((width, index) => {
        if (window.innerWidth > width) {
          breakpointIndex = index
        }
      })
    }

    itemsPerSlide =
      initialProps.itemsPerSlide[breakpointIndex] ||
      initialProps.itemsPerSlide[initialProps.itemsPerSlide.length - 1] ||
      1
  }

  const totalSlides =
    itemsPerSlide > 0 ? Math.ceil(initialProps.items.length / itemsPerSlide) : 0

  const showControls = !(
    initialProps.hideControlsOnSingleSlide && totalSlides < 2
  )

  const currentSlide = initialProps.slideTo ? initialProps.slideTo : 0

  return {
    totalSlides,
    itemsPerSlide,
    showControls,
    currentSlide,
    slideWidth: 0,
    containerWidth: 0,
    paused: false,
    autoPlay: false,
    loopSlides: false,
    slideTo: currentSlide,
  }
}

interface ControlButtonProps {
  direction: 'left' | 'right'
  controls?: {
    visible?: BreakpointArray<boolean>
    placement?: CarouselButtonPlacement
    size?: number
    alternativeChevronStyling?: boolean
    controlBorderWidth?: string
    controlBorderColour?: Property.Color
    controlDisabledBackgroundColor?: Property.Color
    controlHoverBackgroundColor?: Property.Color
  }
  controlSize?: number
  chevronIconStyle?: ChevronIconStyle
  enableWhiteControls?: boolean
  controlPlacement: CarouselButtonPlacement
  disable: boolean
  i18nText?: string
  handleNav: (side: 'left' | 'right') => void
}

const ControlButton: React.FC<ControlButtonProps> = ({
  direction,
  controls,
  controlSize,
  chevronIconStyle,
  enableWhiteControls,
  controlPlacement,
  disable,
  i18nText,
  handleNav,
}) => {
  return (
    <Control
      disabled={disable}
      onClick={() => handleNav(direction)}
      aria-label={i18nText}
      data-testid={`split-control-${direction}`}
      controlBorderColour={controls?.controlBorderColour}
      controlHoverBackgroundColor={controls?.controlHoverBackgroundColor}
      buttonPlacement={controlPlacement}
      position={
        direction === 'left'
          ? HorizontalAlignment.LEFT
          : HorizontalAlignment.RIGHT
      }
      visible={controls?.visible || [true]}
      size={controls?.size}
      controlSize={controlSize}
      alternativeDisabledStyling={controls?.alternativeChevronStyling}
      enableWhiteControls={enableWhiteControls}
    >
      {direction === 'left' ? (
        <React.Fragment>
          {chevronIconStyle?.chevronIcon?.left?.svgPath ? (
            <SvgIcon
              xmlns="http://www.w3.org/2000/svg"
              viewBox={chevronIconStyle.chevronIcon.viewBox}
              width={chevronIconStyle.chevronIcon.width}
              height={chevronIconStyle.chevronIcon.height}
            >
              <path
                d={chevronIconStyle.chevronIcon.left.svgPath}
                fillRule="evenodd"
              />
            </SvgIcon>
          ) : (
            <ChevronLeft width="24" height="24" />
          )}
        </React.Fragment>
      ) : (
        <React.Fragment>
          {chevronIconStyle?.chevronIcon?.right?.svgPath ? (
            <SvgIcon
              xmlns="http://www.w3.org/2000/svg"
              viewBox={chevronIconStyle.chevronIcon.viewBox}
              width={chevronIconStyle.chevronIcon.width}
              height={chevronIconStyle.chevronIcon.height}
            >
              <path
                d={chevronIconStyle.chevronIcon.right.svgPath}
                fillRule="evenodd"
              />
            </SvgIcon>
          ) : (
            <ChevronRight width="24" height="24" />
          )}
        </React.Fragment>
      )}
    </Control>
  )
}

export const Carousel = ({
  itemsPerSlide,
  items,
  i18n = {
    leftScrollLabel: '',
    rightScrollLabel: '',
    playPauseLabel: '',
    closeI18nText: {
      closeAriaLabel: '',
      closeLabel: '',
    },
  },
  hideSlidePreview,
  autoPlay = false,
  indicatorStyle = 'dot',
  dotHorizontalMargin = 1,
  autoPlaySlideDuration = 5000,
  hideControlsOnSingleSlide = true,
  inactiveSlideFullOpacity = true,
  hideControls,
  loopSlides = false,
  slideTo = 0,
  onChange,
  className,
  indicatorWrapperMargin,
  fullWidthCarousel,
  removeControlsOnSingleSlide,
  indicatorPadding,
  mobileImageCrop,
  enableCarouselSignifier,
  enableCarouselWrapAround,
  ...props
}: CarouselProps) => {
  const [prefersReducedMotion, setPrefersReducedMotion] = React.useState(false)

  React.useEffect(() => {
    if (typeof window === 'undefined') return

    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)')
    setPrefersReducedMotion(mediaQuery.matches)
  }, [])

  const [state, dispatch] = React.useReducer(carouselReducer, {
    ...getInitialState({
      items,
      itemsPerSlide,
      hideSlidePreview,
      hideControlsOnSingleSlide,
      slideTo,
      hideControls,
    }),
    onChange,
    loopSlides: (!prefersReducedMotion && autoPlay) || loopSlides,
  })

  React.useEffect(() => {
    dispatch({
      hideSlidePreview,
      hideControlsOnSingleSlide,
      itemsPerSlide,
      type: CarouselReducerActionTypes.Reset,
      containerWidth: containerRef.current?.clientWidth || 0,
      itemsLength: items.length,
    })
    previousSlideTo.current = 0
  }, [items.length])

  React.useEffect(() => {
    if (prefersReducedMotion && autoPlay) {
      dispatch({ type: CarouselReducerActionTypes.Pause })
      clearTimeout(timeOutRef.current)
    }
  }, [prefersReducedMotion, autoPlay])

  const previousSlideTo = React.useRef(slideTo)

  const [slideHeight, setSlideHeight] = React.useState('100%')
  const isAnimating = React.useRef(false)

  const carouselRef = React.useRef<HTMLDivElement>(null)

  const enableWrapAround = items.length > 1 ? enableCarouselWrapAround : false

  const { swipeHandlers, swipeOffset } = useSwipe({
    dispatch,
    enableWrapAround,
    carouselRef,
    onSwipe: props.onSwipe,
    prevSlide: previousSlideTo,
    totalSlides: state.totalSlides,
  })

  const [containerDimensions, setContainerDimensions] = React.useState<
    ElementProp
  >({
    height: 0,
    width: 0,
  })

  const containerRef = React.createRef<HTMLDivElement>()
  const carouselItemsRef = React.useRef<(HTMLLIElement | null)[]>([])

  const pageIndicators = React.useMemo(
    () =>
      indicatorStyle === 'hidden'
        ? null
        : [...Array(state.totalSlides)].map((_, index) => (
            <PageIndicator
              data-testid={`page-indicator-${index}`}
              active={index === state.currentSlide}
              indicatorStyle={indicatorStyle}
              dotHorizontalMargin={dotHorizontalMargin}
              key={index}
              enableWhiteControls={props.enableWhiteControls}
              enableCarouselSignifier={enableCarouselSignifier}
              highlightPageIndicator={props.highlightPageIndicator}
            />
          )),
    [
      indicatorStyle,
      state.totalSlides,
      state.currentSlide,
      dotHorizontalMargin,
      props.enableWhiteControls,
    ],
  )

  const timeOutRef = React.useRef<NodeJS.Timeout>()

  React.useEffect(() => {
    if (autoPlay && !state.paused) {
      timeOutRef.current = setTimeout(
        () => dispatch({ type: CarouselReducerActionTypes.SlideRight }),
        autoPlaySlideDuration,
      )
      return () => clearTimeout(timeOutRef.current)
    }
    return
  }, [autoPlay, autoPlaySlideDuration, state.currentSlide, state.paused])

  React.useEffect(() => {
    if (
      slideTo !== undefined &&
      slideTo !== state.currentSlide &&
      slideTo !== previousSlideTo.current
    ) {
      enableWrapAround &&
        carouselRef.current &&
        wrapSwipe(
          previousSlideTo.current,
          slideTo,
          state.totalSlides,
          carouselRef,
        )
      previousSlideTo.current = slideTo
      dispatch({
        type: CarouselReducerActionTypes.SlideTo,
        toIndex: slideTo,
      })
    }
  }, [slideTo])

  React.useEffect(() => {
    const element = carouselItemsRef.current[state.currentSlide]
      ?.firstChild as HTMLElement | null
    if (!element) return

    const debouncedResize = debounce((entries: ResizeObserverEntry[]) => {
      React.startTransition(() => {
        const { height, width } = entries[0]?.contentRect
        setContainerDimensions((prev) => {
          if (prev.height === height && prev.width === width) return prev
          return { height, width }
        })
      })
    }, DEBOUNCE_INTERVAL_MS)

    requestAnimationFrame(() => {
      if (props.shrinkable) {
        setSlideHeight(`${element.clientHeight}px`)
      }
    })

    return () => {
      debouncedResize.cancel()
    }
  }, [state.currentSlide, props.shrinkable])

  const resizeHandler = React.useCallback(
    (width) => {
      requestAnimationFrame(() => {
        dispatch({
          hideSlidePreview,
          hideControlsOnSingleSlide,
          itemsPerSlide,
          type: CarouselReducerActionTypes.Resize,
          containerWidth: width,
          itemsLength: items.length,
        })
      })
    },
    [hideControlsOnSingleSlide, hideSlidePreview, items.length, itemsPerSlide],
  )

  const togglePause = React.useCallback(() => {
    dispatch({ type: CarouselReducerActionTypes.Pause })
    if (state.paused) {
      timeOutRef.current = setTimeout(
        () => dispatch({ type: CarouselReducerActionTypes.SlideRight }),
        autoPlaySlideDuration,
      )
    } else {
      clearTimeout(timeOutRef.current)
    }
  }, [state.paused, autoPlaySlideDuration])

  const handleNav = React.useCallback(
    (side: 'left' | 'right') => {
      if (isAnimating.current) return
      isAnimating.current = true

      previousSlideTo.current += carouselDirections[side].slide
      dispatch({ type: carouselDirections[side].action })
      props.onNavClick?.(side)

      requestAnimationFrame(() => {
        isAnimating.current = false
      })
    },
    [props.onNavClick],
  )

  if (typeof itemsPerSlide === 'number' && itemsPerSlide <= 0) {
    console.warn('Carousel: no items per slide, cannot divide by 0')
    return null
  }

  // @TODO: [REBUILD-6060] remove carousel buttonPlacement
  const controlPlacement = props.controls?.placement || props.buttonPlacement

  const controlBottomPlacementMap = {
    [CarouselButtonPlacement.BottomMiddle]: {
      horizontalAlignment: HorizontalAlignment.CENTER,
      colStart: 2,
    },
    [CarouselButtonPlacement.BottomReversed]: {
      horizontalAlignment: HorizontalAlignment.LEFT,
      colStart: 1,
    },
    [CarouselButtonPlacement.BottomRight]: {
      horizontalAlignment: HorizontalAlignment.RIGHT,
      colStart: 3,
    },
  }

  const controlBottomPlacement = controlPlacement
    ? controlBottomPlacementMap[controlPlacement]
    : null

  const itemGapSpacing = props.ignoreGapOffset ? 0 : props.itemGapSpacing

  const Controls = (
    <ControlsComponent
      showControls={state.showControls}
      controlPlacement={controlPlacement}
      removeControlsOnSingleSlide={removeControlsOnSingleSlide}
      autoPlay={autoPlay}
      togglePause={togglePause}
      i18n={i18n}
      controls={props.controls}
      controlSize={props.controlSize}
      enableWhiteControls={props.enableWhiteControls}
      isPaused={state.paused}
      currentSlide={state.currentSlide}
      totalSlides={state.totalSlides}
      loopSlides={loopSlides}
      onNavClick={props.onNavClick}
      dispatch={dispatch}
      disabled={autoPlay || loopSlides ? false : state.currentSlide <= 0}
      previousSlideTo={previousSlideTo}
      chevronIconStyle={props.chevronIconStyle}
      pageIndicators={pageIndicators}
      enableWrapAround={enableWrapAround}
      carouselRef={carouselRef}
      highlightPageIndicator={props.highlightPageIndicator}
    />
  )

  const carousel = (
    <React.Fragment>
      <Container
        key="carousel_container"
        ref={containerRef}
        className={className}
        {...(!props.swipeable ? {} : swipeHandlers)}
      >
        {controlPlacement === CarouselButtonPlacement.Split &&
        state.showControls ? (
          <React.Fragment>
            <ControlButton
              controls={props.controls}
              controlSize={props.controlSize}
              controlPlacement={controlPlacement}
              chevronIconStyle={props.chevronIconStyle}
              enableWhiteControls={props.enableWhiteControls}
              direction="left"
              disable={autoPlay ? false : state.currentSlide <= 0}
              i18nText={i18n?.leftScrollLabel}
              handleNav={handleNav}
            />
            <ControlButton
              controls={props.controls}
              controlSize={props.controlSize}
              controlPlacement={controlPlacement}
              chevronIconStyle={props.chevronIconStyle}
              enableWhiteControls={props.enableWhiteControls}
              direction="right"
              disable={
                autoPlay ? false : state.currentSlide >= state.totalSlides - 1
              }
              i18nText={i18n?.rightScrollLabel}
              handleNav={handleNav}
            />
          </React.Fragment>
        ) : (
          (!controlPlacement ||
            controlPlacement === CarouselButtonPlacement.BottomRightFloat ||
            controlPlacement === CarouselButtonPlacement.MiddleRight) &&
          state.showControls && (
            <ControlsWrapper
              hideControls={hideControls}
              controlPlacement={controlPlacement}
            >
              {Controls}
            </ControlsWrapper>
          )
        )}
        <OverflowWrapper
          overflow={props.overflow}
          mobileImageCrop={mobileImageCrop}
        >
          <CarouselWrapper
            ref={carouselRef}
            translateXCurrentSlide={
              -(props.customTransform || 100) * state.currentSlide
            }
            translateXUserSlide={swipeOffset}
            itemGapSpacing={itemGapSpacing}
            itemsPerSlide={itemsPerSlide}
            itemCount={items.length}
            enableSlidePreview={props.enableSlidePreview}
            enableDualSidePreview={props.enableDualSidePreview}
            currentSlide={state.currentSlide}
            overflow={props.overflow}
            reducedMotion={prefersReducedMotion}
            wrapAround={enableWrapAround}
          >
            <CarouselItemContainer
              itemsPerSlide={itemsPerSlide}
              itemCount={items.length}
              itemGapSpacing={itemGapSpacing}
              style={{
                display: 'flex',
                height: slideHeight,
                transformStyle: 'preserve-3d',
                justifyContent: state.totalSlides < 2 ? 'center' : undefined,
              }}
              overflow={props.overflow}
            >
              <CarouselItems
                items={items}
                state={state}
                containerRef={containerRef}
                carouselItemsRef={carouselItemsRef}
                dispatch={dispatch}
                hideSlidePreview={hideSlidePreview}
                isZoomModal={props.isZoomModal}
                zoomClickable={props.zoomClickable}
                inactiveSlideFullOpacity={inactiveSlideFullOpacity}
                itemsPerSlide={itemsPerSlide}
                itemCount={items.length}
                overflow={props.overflow}
                enableSlidePreview={props.enableSlidePreview}
                enableDualSidePreview={props.enableDualSidePreview}
                fullWidthCarousel={fullWidthCarousel}
                zoomOnClick={props.zoomOnClick}
                hideControlsOnSingleSlide={hideControlsOnSingleSlide}
                itemGapSpacing={props.itemGapSpacing}
                enableWrapAround={enableWrapAround}
              />
            </CarouselItemContainer>
          </CarouselWrapper>
        </OverflowWrapper>
      </Container>
      <PageIndicatorGrid
        columns={props.highlightPageIndicator ? 1 : 3}
        key="page-indicators"
        indicatorStyle={indicatorStyle}
        marginTop={containerDimensions.height}
        containerWidth={containerDimensions.width}
        margin={indicatorWrapperMargin}
        indicatorPadding={indicatorPadding}
        enableCarouselSignifier={enableCarouselSignifier}
      >
        {controlPlacement !== CarouselButtonPlacement.BottomMiddle && (
          <PageIndicatorGridItem
            colStart={
              controlPlacement === CarouselButtonPlacement.BottomReversed
                ? [3, 2, 2, 2]
                : controlPlacement === CarouselButtonPlacement.BottomRight
                ? [1, 2, 2, 2]
                : 2
            }
          >
            <PageIndicatorWrapper
              data-testid="page-indicators"
              mobileHorizontalAlignment={
                controlPlacement === CarouselButtonPlacement.BottomReversed
                  ? HorizontalAlignment.RIGHT
                  : controlPlacement === CarouselButtonPlacement.BottomRight
                  ? HorizontalAlignment.LEFT
                  : HorizontalAlignment.CENTER
              }
              hide={state.totalSlides < 2}
              margin={indicatorWrapperMargin}
              enableCarouselSignifier={enableCarouselSignifier}
              highlightPageIndicator={props.highlightPageIndicator}
            >
              {pageIndicators}
            </PageIndicatorWrapper>
          </PageIndicatorGridItem>
        )}
        {controlBottomPlacement && (
          <BottomControlsWrapper
            horizontalAlignment={controlBottomPlacement.horizontalAlignment}
            colStart={controlBottomPlacement.colStart}
            highlightPageIndicator={props.highlightPageIndicator}
          >
            {Controls}
          </BottomControlsWrapper>
        )}
      </PageIndicatorGrid>
    </React.Fragment>
  )

  return (
    <ReactResizeDetector
      handleWidth
      refreshMode="debounce"
      refreshRate={150}
      onResize={resizeHandler}
    >
      {carousel}
    </ReactResizeDetector>
  )
}
